import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  ArrowLeft,
  ClipboardList,
  ShieldCheck,
  ExternalLink,
  Pen,
  Send,
  Undo2,
} from 'lucide-react';
import { Button } from '@roger/ui-components';
import {
  getPatientCommunication,
  getThirtyDaySummaryPdf,
  signTask,
  approveTask,
  breakTaskSignatures,
  getTaskSignatures,
  returnTaskToClinician,
} from '../../services/api';
import type {
  PatientCommunicationResponse,
  TaskSignatureResponse,
} from '../../services/api/types.gen';
import { PermissionSlug } from '../../constants/featureFlags';
import { useStore } from '../../store';
import { getBusinessErrorMessage } from '../../utils/apiErrorDetail';
import { usePdfTab } from '../../hooks/usePdfTab';
import { useToast } from '../../hooks/useToastContext';
import { useAllowSubmitWithoutSignature } from '../../hooks/useAllowSubmitWithoutSignature';
import { AttestationDialog } from '../signing/AttestationDialog';
import type {
  ESignatureCredentialInput,
  SignatureDateInput,
} from '../signing/esignatureCredentialApi';
import { selectAttestationForFamily } from '../signing/attestations';
import { useActiveAttestations } from '../signing/useActiveAttestations';
import { signerName } from '../signing/signatureSummary';
import {
  getSignButtonLabel,
  useTaskPermissions,
} from '../task/TaskDetailPanel';
import { EDITABLE_STATUSES } from '../../constants/statuses';
import {
  ThirtyDaySummaryForm,
  type ThirtyDaySummaryFormHandle,
} from './ThirtyDaySummaryForm';
import { THIRTY_DAY_SUMMARY_COMMUNICATION_TYPE } from './patientCommunicationTypes';
import { ReadonlyPdfView } from '../documents/ReadonlyPdfView';
import EpisodeNotes from '../episode/EpisodeNotes';
import { AutoSaveStatusIndicator } from '../AutoSaveStatusIndicator';
import { WorkLogDialog } from '../task/WorkLogDialog';
import type { AutoSaveStatus } from '../../hooks/useAutoSave';

interface ThirtyDaySummaryDetailPanelProps {
  communicationId: string;
  /** Only used to resolve the signing date in the patient's clinic zone. */
  patientId?: string;
  onBack: () => void;
}

export const ThirtyDaySummaryDetailPanel: React.FC<
  ThirtyDaySummaryDetailPanelProps
> = ({ communicationId, patientId, onBack }) => {
  const user = useStore(state => state.user);
  const canViewWorkLog = useStore(state =>
    state.hasPermission(PermissionSlug.WORKLOG_VIEW)
  );
  const [workLogOpen, setWorkLogOpen] = useState(false);
  const allowUnsignedMoves = useAllowSubmitWithoutSignature();
  const { error: showError, success: showSuccess } = useToast();
  const openPdfTab = usePdfTab();
  const { attestations } = useActiveAttestations();

  const communicationAttestation = useMemo(
    () =>
      attestations
        ? selectAttestationForFamily(attestations, 'communication')
        : null,
    [attestations]
  );

  const [communication, setCommunication] =
    useState<PatientCommunicationResponse | null>(null);
  const [signatures, setSignatures] = useState<TaskSignatureResponse[]>([]);
  const [loading, setLoading] = useState(true);

  const summaryFormRef = useRef<ThirtyDaySummaryFormHandle>(null);
  const [signOpen, setSignOpen] = useState(false);
  const [approveOpen, setApproveOpen] = useState(false);
  const [signing, setSigning] = useState(false);
  const [approving, setApproving] = useState(false);
  const [breaking, setBreaking] = useState(false);
  const [returning, setReturning] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  // Mirrors the form's autosave state so the indicator can live in the header
  // (always visible) instead of scrolling away inside the form body. ROG-2665.
  const [saveStatus, setSaveStatus] = useState<AutoSaveStatus>('idle');

  // Role-based action gating, shared with Visit/Order panels so a 30-Day
  // Summary signs, approves and unlocks exactly like every other task.
  const {
    canSign,
    canApprove,
    canBreakSignatures,
    canReturnToClinician,
    signRequiresESignature,
    signTargetStatus,
  } = useTaskPermissions(user, communication ?? undefined, {
    allowSubmitWithoutSignature: allowUnsignedMoves,
  });

  // The form is editable only before any signature exists — mirroring how
  // Visits/Orders lock once signed (EDITABLE_STATUSES). Everything else renders
  // the same fields read-only.
  const isEditable = communication
    ? EDITABLE_STATUSES.has(communication.status)
    : false;

  const activeSignatures = useMemo(
    () => signatures.filter(sig => !sig.invalidated_at),
    [signatures]
  );

  const signatureSummary = useMemo(
    () =>
      activeSignatures.map(signature => {
        const metadata =
          (signature.signature_metadata as Record<string, unknown> | null) ??
          {};
        const displayName = signerName(signature);
        const credentials = metadata.signer_credentials as
          string | undefined | null;
        const role = metadata.signer_role as string | undefined;
        return {
          id: signature.id,
          role:
            role === 'case_manager'
              ? 'Case Manager'
              : role === 'clinician'
                ? 'Clinician'
                : 'Signer',
          name: credentials ? `${displayName}, ${credentials}` : displayName,
        };
      }),
    [activeSignatures]
  );

  // Load the communication and its signatures by id so the panel works on
  // direct navigation / refresh, mirroring how OrderDetailPanel loads its order.
  const loadCommAndSignatures = useCallback(async () => {
    const [commRes, sigsRes] = await Promise.all([
      getPatientCommunication({
        path: { communication_id: communicationId },
      }),
      getTaskSignatures({ path: { task_id: communicationId } }),
    ]);
    if (commRes.data) setCommunication(commRes.data);
    if (sigsRes.data) setSignatures(sigsRes.data);
  }, [communicationId]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        await loadCommAndSignatures();
      } catch (err) {
        if (cancelled) return;
        console.error('Error loading 30-day summary:', err);
        showError('Load Failed', 'Failed to load 30-Day Summary details.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [loadCommAndSignatures, showError]);

  // Resolve the generated PDF for the read-only (signed) view. Re-runs whenever
  // the communication changes (e.g. after signing) so the preview stays current.
  const loadSummaryPdfSource = useCallback(async () => {
    if (!communication) return null;
    const res = await getThirtyDaySummaryPdf({
      path: { communication_id: communication.id },
    });
    if (res.error || !res.data) return null;
    const url = URL.createObjectURL(
      new Blob([res.data as BlobPart], { type: 'application/pdf' })
    );
    return { src: url, cleanup: () => URL.revokeObjectURL(url) };
  }, [communication]);

  const handleOpenPrintView = useCallback(async () => {
    if (!communication || pdfLoading) return;
    const tab = openPdfTab();
    if (!tab) return;
    setPdfLoading(true);
    try {
      // Mirror handleSign: flush any pending debounced autosave so the exported
      // PDF reflects the latest edits. A non-'saved' outcome means the edits did
      // not persist ('error' already toasted from the form's onSave, 'declined'
      // leaves inline validation visible), so close the placeholder tab and bail
      // rather than print stale, server-persisted data. Only relevant while the
      // form is editable.
      const summaryForm = summaryFormRef.current;
      if (isEditable && summaryForm) {
        const outcome = await summaryForm.flush();
        if (outcome !== 'saved') {
          tab.close();
          return;
        }
      }
      const response = await getThirtyDaySummaryPdf({
        path: { communication_id: communication.id },
      });
      if (!response.data) {
        tab.fail(
          '30-day summary PDF',
          new Error('no PDF data received from server')
        );
        return;
      }
      tab.setBlob(
        new Blob([response.data as BlobPart], { type: 'application/pdf' })
      );
    } catch (err) {
      tab.fail('30-day summary PDF', err);
    } finally {
      setPdfLoading(false);
    }
  }, [communication, pdfLoading, openPdfTab, isEditable]);

  const handleSign = useCallback(async () => {
    if (!communication) return;
    // Flush any pending debounced autosave and validate before signing so the
    // backend signs the latest data. Only relevant while the form is editable.
    const summaryForm = summaryFormRef.current;
    if (isEditable && summaryForm) {
      const outcome = await summaryForm.flush();
      // A non-'saved' flush means the latest edits did not persist: 'error'
      // already surfaced a toast from the form's onSave, and 'declined' leaves
      // the inline validation errors visible. Do not sign stale data.
      if (outcome !== 'saved') return;
      const ok = await summaryForm.validate();
      if (!ok) return;
    }
    setSignOpen(true);
  }, [communication, isEditable, showError]);

  const doSign = useCallback(
    async (
      credential: ESignatureCredentialInput,
      signatureDate?: SignatureDateInput
    ) => {
      if (!communication || !communicationAttestation) return;
      setSigning(true);
      try {
        const result = await signTask({
          path: { task_id: communication.id },
          body: {
            ...credential,
            ...signatureDate,
            attestation_version: communicationAttestation.version,
          },
        });
        if (result.error || !result.data) {
          console.error('Error signing 30-day summary:', result.error);
          showError(
            'Sign Failed',
            getBusinessErrorMessage(result) ??
              'Failed to sign. Please try again.'
          );
          return;
        }
        showSuccess('Signed', '30-Day Summary signed.');
        setSignOpen(false);
        await loadCommAndSignatures();
      } catch (err) {
        console.error('Error signing 30-day summary:', err);
        showError('Sign Failed', 'Failed to sign. Please try again.');
      } finally {
        setSigning(false);
        // Not closed here: the success path already did, and closing on
        // failure would discard the e-signature password just typed.
      }
    },
    [
      communication,
      communicationAttestation,
      loadCommAndSignatures,
      showError,
      showSuccess,
    ]
  );

  const doApprove = useCallback(
    async (credential: ESignatureCredentialInput) => {
      if (!communication || !communicationAttestation) return;
      setApproving(true);
      try {
        const result = await approveTask({
          path: { task_id: communication.id },
          body: {
            ...credential,
            attestation_version: communicationAttestation.version,
          },
        });
        if (result.error) {
          console.error('Error approving 30-day summary:', result.error);
          showError(
            'Approve Failed',
            getBusinessErrorMessage(result) ??
              'Failed to complete. Please try again.'
          );
          return;
        }
        showSuccess('Completed', '30-Day Summary approved and completed.');
        setApproveOpen(false);
        await loadCommAndSignatures();
      } catch (err) {
        console.error('Error approving 30-day summary:', err);
        showError('Approve Failed', 'Failed to complete. Please try again.');
      } finally {
        setApproving(false);
        // Not closed here: the success path already did, and closing on
        // failure would discard the e-signature password just typed.
      }
    },
    [
      communication,
      communicationAttestation,
      loadCommAndSignatures,
      showError,
      showSuccess,
    ]
  );

  const handleBreakSignatures = useCallback(async () => {
    if (!communication) return;
    setBreaking(true);
    try {
      const result = await breakTaskSignatures({
        path: { task_id: communication.id },
      });
      if (result.error) {
        console.error('Error unlocking 30-day summary:', result.error);
        showError('Unlock Failed', 'Failed to unlock. Please try again.');
        return;
      }
      await loadCommAndSignatures();
    } catch (err) {
      console.error('Error unlocking 30-day summary:', err);
      showError('Unlock Failed', 'Failed to unlock. Please try again.');
    } finally {
      setBreaking(false);
    }
  }, [communication, loadCommAndSignatures, showError]);

  // ROG-3268 / EMR-1467 — a case manager hands the summary back to the
  // clinician instead of approving it. This panel has no Actions dropdown, so
  // the action sits beside Edit as its own header button.
  const handleReturnToClinician = useCallback(async () => {
    if (!communication) return;
    setReturning(true);
    try {
      const result = await returnTaskToClinician({
        path: { task_id: communication.id },
      });
      if (result.error) {
        console.error('Error returning 30-day summary:', result.error);
        showError(
          'Return Failed',
          getBusinessErrorMessage(result) ??
            'Failed to return the summary. Please try again.'
        );
        return;
      }
      await loadCommAndSignatures();
    } catch (err) {
      console.error('Error returning 30-day summary:', err);
      showError(
        'Return Failed',
        'Failed to return the summary. Please try again.'
      );
    } finally {
      setReturning(false);
    }
  }, [communication, loadCommAndSignatures, showError]);

  if (loading) {
    return (
      <div className='flex items-center justify-center h-64'>
        <div className='text-center'>
          <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4' />
          <p className='text-muted-foreground'>
            Loading 30-Day Summary details...
          </p>
        </div>
      </div>
    );
  }

  if (!communication) {
    return (
      <div className='flex items-center justify-center h-64'>
        <p className='text-muted-foreground'>30-Day Summary not found.</p>
      </div>
    );
  }

  return (
    <div className='flex flex-col h-full'>
      {/* Header */}
      <div className='flex flex-wrap items-center justify-between gap-x-4 gap-y-2 px-5 py-3 border-b bg-muted/20'>
        <div className='flex min-w-0 flex-1 items-center gap-3'>
          <Button
            variant='outline'
            size='sm'
            onClick={onBack}
            className='flex items-center gap-1.5 flex-shrink-0'
          >
            <ArrowLeft className='h-3.5 w-3.5' />
            Back
          </Button>
          <div className='h-4 w-px bg-border' />
          <div className='min-w-0 flex-1'>
            <span className='min-w-0 flex-1 truncate text-sm font-medium text-foreground'>
              {THIRTY_DAY_SUMMARY_COMMUNICATION_TYPE}
            </span>
          </div>
        </div>

        {/* Wraps because a case manager on a submitted summary sees Print
            view, Edit, Return to clinician and Approve at once — more than a
            390px viewport can hold on one line, and flex children do not
            shrink below their content width. */}
        <div className='flex flex-wrap items-center justify-end gap-2'>
          {isEditable && (
            <AutoSaveStatusIndicator status={saveStatus} className='mr-1' />
          )}
          <Button
            variant='outline'
            size='sm'
            onClick={() => void handleOpenPrintView()}
            disabled={pdfLoading}
            className='flex items-center gap-1.5'
          >
            {pdfLoading ? (
              <>
                <div className='animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-current' />
                Opening print view...
              </>
            ) : (
              <>
                <ExternalLink className='h-4 w-4' />
                Print view
              </>
            )}
          </Button>

          {canViewWorkLog && (
            <Button
              variant='outline'
              size='sm'
              onClick={() => setWorkLogOpen(true)}
              className='flex items-center gap-1.5'
            >
              <ClipboardList className='h-4 w-4' />
              Work log
            </Button>
          )}

          {canBreakSignatures && (
            <Button
              variant='outline'
              size='sm'
              onClick={() => void handleBreakSignatures()}
              disabled={breaking}
              className='flex items-center gap-1.5'
            >
              <Pen className='h-4 w-4' />
              {breaking ? 'Unlocking...' : 'Edit'}
            </Button>
          )}

          {canReturnToClinician && (
            <Button
              variant='outline'
              size='sm'
              onClick={() => void handleReturnToClinician()}
              disabled={returning}
              className='flex items-center gap-1.5'
            >
              <Undo2 className='h-4 w-4' />
              {returning ? 'Returning…' : 'Return to clinician'}
            </Button>
          )}

          {canSign && (
            <Button
              size='sm'
              variant='default'
              onClick={() => void handleSign()}
              disabled={signing || !communicationAttestation}
              className='flex items-center gap-1.5'
            >
              {signing ? (
                <>
                  <div className='animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-current' />
                  Signing...
                </>
              ) : (
                <>
                  <ShieldCheck className='h-4 w-4' />
                  Sign &amp; Submit
                </>
              )}
            </Button>
          )}

          {canApprove && (
            <Button
              size='sm'
              variant='default'
              onClick={() => setApproveOpen(true)}
              disabled={approving || !communicationAttestation}
              className='flex items-center gap-1.5'
            >
              {approving ? (
                <>
                  <div className='animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-current' />
                  Approving...
                </>
              ) : (
                <>
                  <Send className='h-4 w-4' />
                  Approve
                </>
              )}
            </Button>
          )}
        </div>
      </div>

      {/* Signer info (read-only) once the summary carries signatures */}
      {signatureSummary.length > 0 && (
        <div className='flex flex-wrap items-center gap-x-6 gap-y-1 px-5 py-2 border-b bg-muted/10 text-xs text-muted-foreground'>
          {signatureSummary.map(sig => (
            <span key={sig.id}>
              <span className='font-medium text-foreground'>{sig.role}:</span>{' '}
              {sig.name}
            </span>
          ))}
        </div>
      )}

      {/* Body: editable form before signing, inline PDF preview once signed. */}
      <div className='flex-1 overflow-auto p-4 space-y-4'>
        {isEditable ? (
          <ThirtyDaySummaryForm
            ref={summaryFormRef}
            communication={communication}
            onSaveStatusChange={setSaveStatus}
          />
        ) : (
          <div className='h-[calc(100vh-14rem)] min-h-[760px]'>
            <ReadonlyPdfView
              loadSource={loadSummaryPdfSource}
              title='30-Day Summary Preview'
              errorText='Failed to load 30-Day Summary preview.'
            />
          </div>
        )}

        <EpisodeNotes
          episodeId={communication.episode_id}
          description='Shared by every task in this episode.'
        />
      </div>

      <WorkLogDialog
        open={workLogOpen}
        onOpenChange={setWorkLogOpen}
        taskId={communicationId}
        taskType='communication'
      />

      <AttestationDialog
        open={signOpen}
        requiresCredential={signRequiresESignature}
        attestation={communicationAttestation}
        signerName={user?.name ?? null}
        signerEmail={user?.email ?? null}
        isLoading={signing}
        actionLabel={getSignButtonLabel(signTargetStatus)}
        // Clinician lane only -- see the prop's docs on AttestationDialog.
        showSignatureDate={signRequiresESignature}
        patientId={patientId}
        taskId={communicationId}
        onConfirm={(credential, signatureDate) =>
          void doSign(credential, signatureDate)
        }
        onCancel={() => setSignOpen(false)}
      />

      <AttestationDialog
        open={approveOpen}
        requiresCredential={false}
        busyLabel='Approving…'
        attestation={communicationAttestation}
        signerName={user?.name ?? null}
        signerEmail={user?.email ?? null}
        isLoading={approving}
        actionLabel='Approve'
        onConfirm={credential => void doApprove(credential)}
        onCancel={() => setApproveOpen(false)}
      />
    </div>
  );
};
